import React, { useState, useEffect } from 'react';
import { Store, Smile } from 'lucide-react';
import EmojiPicker from './EmojiPicker';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const InternalShopForm = ({ shop, onSave, onCancel, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    icon: '🏪',
    isActive: true
  });
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    if (shop) {
      setFormData({
        name: shop.name || '',
        description: shop.description || '',
        category: shop.category || '',
        icon: shop.icon || '🏪',
        isActive: shop.is_active !== undefined ? shop.is_active : true
      });
    }
  }, [shop]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validierung
    if (!formData.name.trim()) {
      if (showWarning) {
        showWarning(t('admin.shopForm.nameRequired'));
      } else {
        alert(t('admin.shopForm.nameRequired'));
      }
      return;
    }

    onSave(formData);
  };

  return (
    <div className="relative group">
      {/* Glass Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 backdrop-blur-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
        : 'bg-white border border-slate-200 shadow-sm'
      } rounded-3xl`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>

      <div className="relative p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="relative group/icon">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent'
                : 'hidden'
              } rounded-2xl blur-sm`}></div>
              <div className={`relative ${isDarkMode
                ? 'bg-gradient-to-br from-emerald-600/60 via-emerald-700/50 to-emerald-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(16,185,129,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                : 'bg-brand/10'
              } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                <Store className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {shop ? t('admin.shopForm.editTitle') : t('admin.shopForm.createTitle')}
              </h2>
              <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('admin.shopForm.subtitle')}</p>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onCancel}
            className={`relative group/close overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.05] active:scale-[0.95] ${
              isDarkMode ? '' : 'bg-slate-100 hover:bg-red-50 border border-slate-200 hover:border-red-200'
            }`}
            title={t('common.close')}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(71,85,105,0.4) 0%, rgba(75,85,99,0.4) 50%, rgba(71,85,105,0.4) 100%)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
            } : {}}
          >
            {/* Hover Overlay (nur Dark) */}
            <div
              className={`absolute inset-0 rounded-xl transition-all duration-300 opacity-0 group-hover/close:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
              style={{
                background: 'linear-gradient(135deg, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.2) 50%, rgba(239,68,68,0.15) 100%)'
              }}
            ></div>

            <div className="relative p-2">
              <span className={`transition-all duration-300 font-bold text-lg ${
                isDarkMode ? 'text-white/70 group-hover/close:text-red-300' : 'text-slate-600 group-hover/close:text-red-600'
              } group-hover/close:scale-110 inline-block`}>✕</span>
            </div>

            {/* Wave Effect (nur Dark) */}
            <div className={`absolute inset-0 opacity-0 group-hover/close:opacity-100 transition-opacity duration-400 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/close:translate-x-full transition-transform duration-600"
                style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.2) 50%, transparent 100%)' }}
              ></div>
            </div>
          </button>
        </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Shop Name Field */}
          <div className="relative group/input">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'} mb-3`}>{t('admin.shopForm.name')}</label>
            <div className="relative">
              {/* Input Background */}
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>

              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                  isDarkMode
                    ? 'text-white placeholder-white/60 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-brand/40'
                }`}
                placeholder={t('admin.shopForm.namePlaceholder')}
              />

              {/* Focus Highlight */}
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${
                isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'
              } transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>

          {/* Category Field */}
          <div className="relative group/input">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'} mb-3`}>{t('admin.shopForm.category')}</label>
            <div className="relative">
              {/* Input Background */}
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>

              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                  isDarkMode
                    ? 'text-white placeholder-white/60 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-brand/40'
                }`}
                placeholder={t('admin.shopForm.categoryPlaceholder')}
              />

              {/* Focus Highlight */}
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${
                isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'
              } transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>
        </div>

        {/* Description Field */}
        <div className="relative group/input">
          <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'} mb-3`}>{t('admin.shopForm.description')}</label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gray-800/30 backdrop-blur-xl border border-white/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
              : 'bg-white border border-slate-200'
            } rounded-2xl`}></div>

            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
              className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                isDarkMode
                  ? 'text-white placeholder-white/60 focus:ring-blue-400/50'
                  : 'text-slate-900 placeholder-slate-400 focus:ring-brand/40'
              } resize-none`}
              placeholder={t('admin.shopForm.descriptionPlaceholder')}
            />

            {/* Focus Highlight */}
            <div className={`absolute inset-0 rounded-2xl border border-transparent ${
              isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'
            } transition-all duration-300 pointer-events-none`}></div>
          </div>
        </div>

        {/* Shop Icon Field */}
        <div>
          <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'} mb-3`}>{t('admin.shopForm.icon')}</label>
          <div className="flex items-center space-x-3">
            {/* Current Icon Display */}
            <div className="relative group/iconpicker">
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-br from-white/20 via-white/10 to-transparent' : 'hidden'} rounded-2xl blur-sm`}></div>
              <div
                className={`relative ${isDarkMode
                  ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                  : 'bg-brand/10 border border-brand/20'
                } w-12 h-12 rounded-2xl flex items-center justify-center cursor-pointer hover:scale-105 transition-all duration-300`}
                onClick={() => setShowEmojiPicker(true)}
                title={t('admin.shopForm.selectIcon')}
              >
                <span className="text-2xl relative z-10">{formData.icon}</span>
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>

            {/* Emoji Picker Button */}
            <button
              type="button"
              onClick={() => setShowEmojiPicker(true)}
              className={`relative group/picker flex-1 overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700'
              }`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              {/* Hover Overlay (nur Dark) */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/picker:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)' }}
              ></div>

              <div className={`relative z-10 px-4 py-3 flex items-center justify-center font-medium transition-all duration-300 ${
                isDarkMode ? 'text-white' : 'text-slate-700'
              }`}>
                <Smile className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/picker:scale-110" />
                {t('admin.shopForm.selectIcon')}
              </div>

              {/* Wave Effect (nur Dark) */}
              <div className={`absolute inset-0 opacity-0 group-hover/picker:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/picker:translate-x-full transition-transform duration-800"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)' }}
                ></div>
              </div>
            </button>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'} mt-2`}>
            {t('admin.shopForm.iconHint')}
          </p>
        </div>

        {/* Active Checkbox */}
        <div className="relative group/checkbox">
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-br from-gray-900/20 via-gray-800/15 to-gray-900/20 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
            : 'bg-slate-50 border border-slate-200'
          } rounded-2xl`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
            : ''
          } rounded-2xl`}></div>

          <div className="relative p-4 flex items-center">
            <div className="relative">
              {/* Checkbox Glass Background */}
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl border border-white/30 shadow-[0_4px_16px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)]'
                : ''
              } rounded-lg`}></div>
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                className={`relative h-5 w-5 ${isDarkMode
                  ? 'text-blue-400 focus:ring-blue-500/50 border-0 bg-transparent'
                  : 'text-brand focus:ring-brand/40 border-slate-300 rounded'
                } focus:ring-2 transition-all duration-300 cursor-pointer`}
              />
            </div>
            <label htmlFor="isActive" className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'} cursor-pointer`}>
              {t('admin.shopForm.isActive')}
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6">
          {/* Divider */}
          <div className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent mb-6">
            <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-emerald-400/60 to-blue-400/60 blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
          </div>

          {/* Cancel Button */}
          <button
            type="button"
            onClick={onCancel}
            className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
              isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'
            }`}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            } : {}}
          >
            {/* Hover Overlay (nur Dark) */}
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)' }}
            ></div>

            <div className={`relative z-10 px-6 py-3 font-medium transition-all duration-300 ${
              isDarkMode ? 'text-white' : 'text-slate-700'
            }`}>
              {t('common.cancel')}
            </div>

            {/* Wave Effect (nur Dark) */}
            <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)' }}
              ></div>
            </div>
          </button>

          {/* Submit Button */}
          <button
            type="submit"
            className="relative group/submit overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99]"
            style={{
              background: isDarkMode
                ? (shop
                    ? 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(21,128,61,0.7) 50%, rgba(22,101,52,0.7) 100%)'
                    : 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)')
                : (shop ? '#16A34A' : '#2563EB'),
              backdropFilter: isDarkMode ? 'blur(20px)' : 'none',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : 'none',
              boxShadow: isDarkMode
                ? (shop
                    ? '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)')
                : 'none'
            }}
          >
            {/* Hover Overlay */}
            <div
              className="absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/submit:opacity-100"
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                  : 'rgba(255,255,255,0.12)'
              }}
            ></div>

            <div className="relative z-10 px-6 py-3 font-medium text-white transition-all duration-300">
              {shop ? t('common.update') : t('common.create')}
            </div>

            {/* Wave Effect (nur Dark) */}
            <div className={`absolute inset-0 opacity-0 group-hover/submit:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/submit:translate-x-full transition-transform duration-800"
                style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)' }}
              ></div>
            </div>
          </button>
        </div>
      </form>

        {/* Emoji Picker Modal */}
        {showEmojiPicker && (
          <EmojiPicker
            currentEmoji={formData.icon}
            onSelect={(emoji) => {
              setFormData({...formData, icon: emoji});
              setShowEmojiPicker(false);
            }}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>
    </div>
  );
};

export default InternalShopForm;
